import { useMemo, useState } from 'react';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/lib/useMediaQuery';
import { AvatarStack } from '@/components/projects/ProjectVisuals';
import { PRIORITY_META, STATUS_META } from '@/features/projects/display';
import {
  useCreateTask,
  type TaskWithAssignees,
} from '@/features/projects/hooks';
import { WorkloadHoursDraftInput } from '@/components/projects/WorkloadHoursInput';
import {
  formatWorkloadHours,
  isHeavyDay,
  parseWorkloadHours,
  sumWorkloadHours,
  WORKDAY_HOURS,
} from '@/features/projects/workload';

// ---------------------------------------------------------------------------
// Local-date helpers — same approach as the team CalendarPage and the
// social planner: everything keys on LOCAL `YYYY-MM-DD` strings because
// tasks.due_date is a plain date column.
// ---------------------------------------------------------------------------

function toKey(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABEL = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
});
const AGENDA_LABEL = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'short',
  day: 'numeric',
});
const MAX_MOBILE_DOTS = 3;

function parseKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

export function ProjectTaskCalendar({
  projectId,
  tasks,
  disabled,
  isLoading,
  onOpenTask,
}: {
  projectId: string;
  /** Filtered tasks (parents + visible subtasks) to plot by due date. */
  tasks: TaskWithAssignees[];
  /** True when the project is archived — disables creating tasks. */
  disabled: boolean;
  isLoading: boolean;
  onOpenTask: (taskId: string) => void;
}) {
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  // The day a quick-add dialog is open for (null = closed).
  const [createDate, setCreateDate] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const [selectedDay, setSelectedDay] = useState(() => toKey(new Date()));

  const goToMonth = (next: Date) => {
    setCursor(next);
    const now = new Date();
    const sameMonth =
      next.getFullYear() === now.getFullYear() &&
      next.getMonth() === now.getMonth();
    setSelectedDay(toKey(sameMonth ? now : next));
  };

  const tasksByDay = useMemo(() => {
    const map = new Map<string, TaskWithAssignees[]>();
    for (const t of tasks) {
      if (!t.due_date) continue;
      const list = map.get(t.due_date);
      if (list) list.push(t);
      else map.set(t.due_date, [t]);
    }
    return map;
  }, [tasks]);

  const undatedCount = useMemo(
    () => tasks.filter((t) => !t.due_date).length,
    [tasks],
  );

  // The visible grid spans from the Sunday on/before the 1st to the
  // Saturday on/after the last day of the month.
  const weeks = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const start = addDays(first, -first.getDay());
    const end = addDays(last, 6 - last.getDay());
    const days: Date[] = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
      days.push(new Date(d));
    }
    const wk: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) wk.push(days.slice(i, i + 7));
    return wk;
  }, [cursor]);

  const todayStr = toKey(new Date());

  const quickAdd =
    createDate !== null ? (
      <QuickAddTaskDialog
        projectId={projectId}
        initialDate={createDate}
        onClose={() => setCreateDate(null)}
      />
    ) : null;

  if (isMobile) {
    return (
      <>
        <MobileMonthCalendar
          cursor={cursor}
          weeks={weeks}
          tasksByDay={tasksByDay}
          todayStr={todayStr}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
          onPrevMonth={() =>
            goToMonth(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))
          }
          onNextMonth={() =>
            goToMonth(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))
          }
          onToday={() => {
            const now = new Date();
            goToMonth(new Date(now.getFullYear(), now.getMonth(), 1));
          }}
          undatedCount={undatedCount}
          disabled={disabled}
          isLoading={isLoading}
          onOpenTask={onOpenTask}
          onAddTask={setCreateDate}
        />
        {quickAdd}
      </>
    );
  }

  return (
    <div className="space-y-2">
      {/* Month navigation */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Previous month"
            onClick={() =>
              setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))
            }
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const now = new Date();
              setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
            }}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Next month"
            onClick={() =>
              setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))
            }
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <span className="min-w-36 text-sm font-medium text-foreground">
          {MONTH_LABEL.format(cursor)}
        </span>
        {undatedCount > 0 && (
          <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5" />
            {undatedCount} task{undatedCount === 1 ? '' : 's'} without a due
            date not shown
          </span>
        )}
      </div>

      {/* Month grid */}
      <div className="overflow-x-auto">
        <div className="min-w-[760px] overflow-hidden rounded-lg border border-border bg-card">
          <div className="grid grid-cols-7 border-b border-border bg-secondary/40">
            {WEEKDAYS.map((d) => (
              <div
                key={d}
                className="px-2 py-2 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground"
              >
                {d}
              </div>
            ))}
          </div>

          {isLoading ? (
            <div className="grid grid-cols-7 gap-px bg-border">
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="min-h-28 bg-card p-2">
                  <Skeleton className="mb-2 h-4 w-6" />
                  <Skeleton className="h-5 w-full" />
                </div>
              ))}
            </div>
          ) : (
            weeks.map((week, wi) => (
              <div
                key={wi}
                className={cn(
                  'grid grid-cols-7 gap-px bg-border',
                  wi > 0 && 'border-t border-border',
                )}
              >
                {week.map((day) => {
                  const key = toKey(day);
                  const inMonth = day.getMonth() === cursor.getMonth();
                  const isToday = key === todayStr;
                  const dayTasks = tasksByDay.get(key) ?? [];
                  const dayHours = sumWorkloadHours(dayTasks);
                  const heavy = isHeavyDay(dayHours);
                  return (
                    <div
                      key={key}
                      role={disabled ? undefined : 'button'}
                      tabIndex={disabled ? undefined : 0}
                      aria-label={
                        disabled ? undefined : `Add task on ${key}`
                      }
                      onClick={() => {
                        if (!disabled) setCreateDate(key);
                      }}
                      onKeyDown={(e) => {
                        if (!disabled && e.key === 'Enter') setCreateDate(key);
                      }}
                      className={cn(
                        'group min-h-28 space-y-1 p-1.5 transition-colors',
                        !disabled && 'cursor-pointer',
                        inMonth
                          ? 'bg-card hover:bg-secondary/30'
                          : 'bg-secondary/20 hover:bg-secondary/40',
                      )}
                    >
                      <div className="flex items-center justify-between px-0.5">
                        <span
                          className={cn(
                            'flex h-6 w-6 items-center justify-center rounded-full text-xs',
                            isToday
                              ? 'bg-primary font-medium text-primary-foreground'
                              : inMonth
                                ? 'text-foreground'
                                : 'text-muted-foreground/60',
                          )}
                        >
                          {day.getDate()}
                        </span>
                        {dayHours > 0 ? (
                          <span
                            className={cn(
                              'text-[10px] font-medium',
                              heavy
                                ? 'text-fresh-salmon-dark'
                                : 'text-muted-foreground',
                            )}
                            title={
                              heavy
                                ? `${formatWorkloadHours(dayHours)} estimated — over a typical ${WORKDAY_HOURS}h day`
                                : `${formatWorkloadHours(dayHours)} estimated`
                            }
                          >
                            {formatWorkloadHours(dayHours)}
                          </span>
                        ) : !disabled ? (
                          <Plus className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-60" />
                        ) : null}
                      </div>

                      {dayTasks.map((task) => (
                        <TaskChip
                          key={task.id}
                          task={task}
                          onOpen={() => onOpenTask(task.id)}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>

      {quickAdd}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile — compact month grid with priority dots + agenda for one day
// ---------------------------------------------------------------------------

function MobileMonthCalendar({
  cursor,
  weeks,
  tasksByDay,
  todayStr,
  selectedDay,
  onSelectDay,
  onPrevMonth,
  onNextMonth,
  onToday,
  undatedCount,
  disabled,
  isLoading,
  onOpenTask,
  onAddTask,
}: {
  cursor: Date;
  weeks: Date[][];
  tasksByDay: Map<string, TaskWithAssignees[]>;
  todayStr: string;
  selectedDay: string;
  onSelectDay: (key: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  undatedCount: number;
  disabled: boolean;
  isLoading: boolean;
  onOpenTask: (taskId: string) => void;
  onAddTask: (key: string) => void;
}) {
  const agendaTasks = tasksByDay.get(selectedDay) ?? [];
  const agendaHours = sumWorkloadHours(agendaTasks);
  const agendaHeavy = isHeavyDay(agendaHours);

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center gap-1 border-b border-border px-1.5 py-1.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11"
            aria-label="Previous month"
            onClick={onPrevMonth}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <span className="flex-1 text-center text-base text-foreground">
            {MONTH_LABEL.format(cursor)}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11"
            aria-label="Next month"
            onClick={onNextMonth}
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        <div className="grid grid-cols-7 px-1 pt-2">
          {WEEKDAYS.map((d) => (
            <div
              key={d}
              className="pb-1 text-center text-[11px] uppercase tracking-wider text-muted-foreground"
            >
              {d.charAt(0)}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-y-0.5 px-1 pb-2">
          {isLoading
            ? Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="flex h-12 items-center justify-center">
                  <Skeleton className="h-7 w-7 rounded-full" />
                </div>
              ))
            : weeks.flat().map((day) => {
                const key = toKey(day);
                const inMonth = day.getMonth() === cursor.getMonth();
                const isToday = key === todayStr;
                const isSelected = key === selectedDay;
                const dayTasks = tasksByDay.get(key) ?? [];
                const heavy = isHeavyDay(sumWorkloadHours(dayTasks));
                const extra = dayTasks.length - MAX_MOBILE_DOTS;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onSelectDay(key)}
                    aria-pressed={isSelected}
                    aria-label={`${AGENDA_LABEL.format(day)}${
                      dayTasks.length > 0
                        ? `, ${dayTasks.length} task${dayTasks.length === 1 ? '' : 's'}`
                        : ''
                    }`}
                    className={cn(
                      'mx-auto flex h-12 w-full max-w-12 flex-col items-center justify-start gap-1 rounded-lg pt-1 transition-colors',
                      isSelected
                        ? 'bg-info-soft/30 ring-1 ring-inset ring-info'
                        : 'active:bg-secondary',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-full text-sm',
                        isToday
                          ? 'bg-primary font-medium text-primary-foreground'
                          : heavy
                            ? 'text-fresh-salmon-dark'
                            : inMonth
                              ? 'text-foreground'
                              : 'text-muted-foreground/50',
                      )}
                    >
                      {day.getDate()}
                    </span>
                    <span className="flex h-1.5 items-center gap-0.5" aria-hidden>
                      {dayTasks.slice(0, MAX_MOBILE_DOTS).map((t) => (
                        <span
                          key={t.id}
                          className={cn(
                            'h-1.5 w-1.5 rounded-full',
                            t.is_completed
                              ? 'bg-bright-moss-dark/50'
                              : PRIORITY_META[t.priority].dot,
                          )}
                        />
                      ))}
                      {extra > 0 && (
                        <span className="text-[9px] leading-none text-muted-foreground">
                          +{extra}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
        </div>
      </div>

      <section aria-label="Tasks due on selected day" className="space-y-2">
        <div className="flex items-center gap-2">
          <span aria-hidden className="h-4 w-1 rounded-full bg-info" />
          <h3 className="min-w-0 flex-1 truncate text-sm text-foreground">
            {selectedDay === todayStr
              ? `Today · ${AGENDA_LABEL.format(parseKey(selectedDay))}`
              : AGENDA_LABEL.format(parseKey(selectedDay))}
          </h3>
          {agendaHours > 0 && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs',
                agendaHeavy
                  ? 'bg-fresh-salmon/25 text-fresh-salmon-dark'
                  : 'bg-secondary text-muted-foreground',
              )}
              title={
                agendaHeavy
                  ? `Over a typical ${WORKDAY_HOURS}h day`
                  : undefined
              }
            >
              <Clock className="h-3 w-3" aria-hidden />
              {formatWorkloadHours(agendaHours)}
            </span>
          )}
          {selectedDay !== todayStr && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-2.5 text-info"
              onClick={onToday}
            >
              Today
            </Button>
          )}
        </div>

        {agendaTasks.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            Nothing due this day.
          </p>
        ) : (
          <ul className="space-y-2">
            {agendaTasks.map((task) => (
              <li key={task.id}>
                <AgendaRow task={task} onOpen={() => onOpenTask(task.id)} />
              </li>
            ))}
          </ul>
        )}

        {!disabled && (
          <button
            type="button"
            onClick={() => onAddTask(selectedDay)}
            className="flex min-h-11 w-full items-center gap-2 rounded-lg border border-dashed border-border px-3 text-sm text-muted-foreground transition-colors active:bg-secondary"
          >
            <Plus className="h-4 w-4" />
            Add task on {AGENDA_LABEL.format(parseKey(selectedDay))}
          </button>
        )}

        {undatedCount > 0 && (
          <p className="flex items-center gap-1.5 pt-1 text-xs text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5 shrink-0" />
            {undatedCount} task{undatedCount === 1 ? '' : 's'} without a due
            date not shown
          </p>
        )}
      </section>
    </div>
  );
}

function AgendaRow({
  task,
  onOpen,
}: {
  task: TaskWithAssignees;
  onOpen: () => void;
}) {
  const priority = PRIORITY_META[task.priority];
  const status = STATUS_META[task.status];
  const hoursLabel = formatWorkloadHours(task.approximate_workload_hours);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'relative flex min-h-14 w-full items-center gap-3 overflow-hidden rounded-lg border border-border bg-card py-2.5 pl-4 pr-3 text-left transition-colors active:bg-secondary/40',
        task.is_completed && 'bg-secondary/30',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'absolute inset-y-0 left-0 w-1',
          task.is_completed ? 'bg-bright-moss-dark/40' : priority.dot,
        )}
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-sm text-foreground',
            task.is_completed && 'text-muted-foreground line-through',
          )}
        >
          {task.name}
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-1.5">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-[11px]',
              status.className,
            )}
          >
            {status.label}
          </span>
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-[11px]',
              priority.className,
            )}
          >
            {priority.label}
          </span>
          {hoursLabel && (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" aria-hidden />
              {hoursLabel}
            </span>
          )}
        </span>
      </span>
      {task.assignees.length > 0 && (
        <AvatarStack people={task.assignees} max={2} />
      )}
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Task chip — one task on a calendar day
// ---------------------------------------------------------------------------

function TaskChip({
  task,
  onOpen,
}: {
  task: TaskWithAssignees;
  onOpen: () => void;
}) {
  const hoursLabel = formatWorkloadHours(task.approximate_workload_hours);
  const assigneeNames = task.assignees
    .map((a) => a.full_name || a.email)
    .filter(Boolean)
    .join(', ');
  return (
    <button
      type="button"
      onClick={(e) => {
        // Don't let the click bubble to the day cell (which would open
        // the quick-add dialog instead of the task drawer).
        e.stopPropagation();
        onOpen();
      }}
      title={[
        task.name,
        hoursLabel || null,
        STATUS_META[task.status].label,
        `${PRIORITY_META[task.priority].label} priority`,
        task.is_completed ? 'Completed' : null,
        assigneeNames ? `→ ${assigneeNames}` : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      className={cn(
        'block w-full rounded-md border border-border/60 bg-secondary/40 px-1.5 py-1 text-left text-xs transition-colors hover:bg-secondary',
        task.is_completed && 'opacity-60',
      )}
    >
      <span className="flex items-center gap-1.5">
        <span
          className={cn(
            'h-1.5 w-1.5 shrink-0 rounded-full',
            PRIORITY_META[task.priority].dot,
          )}
        />
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            task.is_completed && 'line-through text-muted-foreground',
          )}
        >
          {task.name}
        </span>
        {hoursLabel && (
          <span className="shrink-0 text-[10px] text-muted-foreground">
            {hoursLabel}
          </span>
        )}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Quick-add dialog — create a task pre-filled with the clicked date
// ---------------------------------------------------------------------------

function QuickAddTaskDialog({
  projectId,
  initialDate,
  onClose,
}: {
  projectId: string;
  initialDate: string;
  onClose: () => void;
}) {
  const createTask = useCreateTask();
  const [name, setName] = useState('');
  const [dueDate, setDueDate] = useState(initialDate);
  const [workloadHours, setWorkloadHours] = useState('');

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed || createTask.isPending) return;
    let hours: number | null = null;
    try {
      hours = parseWorkloadHours(workloadHours);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid hours.');
      return;
    }
    createTask.mutate(
      {
        projectId,
        name: trimmed,
        dueDate: dueDate || null,
        approximateWorkloadHours: hours,
      },
      {
        onSuccess: () => onClose(),
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : 'Could not create the task.',
          ),
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Input
            autoFocus
            placeholder="Task name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <DatePicker
            value={dueDate}
            onChange={setDueDate}
            aria-label="Due date"
          />
          <WorkloadHoursDraftInput
            value={workloadHours}
            onChange={setWorkloadHours}
            placeholder="Approx. hours (optional)"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || createTask.isPending}
            >
              {createTask.isPending ? 'Adding…' : 'Add task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
